// src/pages/AdminReportServicePrices.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { NavLink, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import { Card, Modal, Form, Button } from "react-bootstrap";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { Button as Buttonmui } from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";

import { BASE_URL, security_key as SECURITY_KEY } from "../global";

const GRID_HEIGHT = 640;

// Country IDs
const COUNTRY = {
    SINGAPORE: 1,
    UNITED_KINGDOM: 183,
    NEW_ZEALAND: 184,
    AUSTRALIA: 185,
    SCOTLAND: 189,
};

const COUNTRY_CURRENCY_POUNDS = [COUNTRY.UNITED_KINGDOM, COUNTRY.SCOTLAND];

const symbolForCountry = (cent_country) =>
    COUNTRY_CURRENCY_POUNDS.includes(Number(cent_country)) ? "£" : "$";

const formatMoney = (amount, cent_country) => {
    const n = Number(amount) || 0;
    const sym = symbolForCountry(cent_country);
    console.info(`cent_country: ${cent_country} symbol {$sym}`);
    return `${sym}${n.toLocaleString()}`;
};

const formatSubscription = (s) => {
    if (!s) return "— none —";
    return String(s)
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");
};

const endOfMonthStr = (ym) =>
    dayjs(`${ym}-01`).endOf("month").format("YYYY-MM-DD");

export default function AdminReportServicePrices() {
    const location = useLocation();

    const [loading, setLoading] = useState(false);

    const [serviceRowsRaw, setServiceRowsRaw] = useState([]);
    const [licenceRowsRaw, setLicenceRowsRaw] = useState([]);

    const initialMonth = dayjs().format("YYYY-MM");
    const [billMonth, setBillMonth] = useState(initialMonth);
    const [refDate, setRefDate] = useState(endOfMonthStr(initialMonth));
    const [tab, setTab] = useState("trial");

    const [showModalPaid, setShowModalPaid] = useState(false);
    const [isSubmittingPaid, setIsSubmittingPaid] = useState(false);
    const [paidData, setPaidData] = useState(null);
    const toggleModalPaid = () => setShowModalPaid((v) => !v);

    useEffect(() => {
        sessionStorage.setItem("previousPageAdminPathname", location.pathname);
    }, [location.pathname]);

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line
    }, []);


    useEffect(() => {
        const wantRef = endOfMonthStr(billMonth);
        if (refDate !== wantRef) setRefDate(wantRef);
        // eslint-disable-next-line
    }, [billMonth]);


    useEffect(() => {
        const ym = dayjs(refDate).format("YYYY-MM");
        if (billMonth !== ym) setBillMonth(ym);
        // eslint-disable-next-line
    }, [refDate]);


    async function loadAll(date = refDate, month = billMonth) {
        setLoading(true);
        try {
            const res = await axios.post(BASE_URL + "API/api_index.php", {
                security_key: SECURITY_KEY,
                mode: "getCentreServiceAndLicensing",
                ref_date: date,   // YYYY-MM-DD (end of month)
                month,            // YYYY-MM
                fk_cent_key: "",  // all centres (backend already filters to active, non-trial)
            });

            const payload = res?.data?.data || {};
            const svcData = Array.isArray(payload.service_rows) ? payload.service_rows : [];
            const licData = Array.isArray(payload.licence_rows) ? payload.licence_rows : [];

            // service rows
            const mappedSvc = svcData.map((r, i) => ({
                id: `${r.pk_cent_key || i}`,
                pk_cent_key: r.pk_cent_key,
                cent_name: r.cent_name,
                cent_country: Number(r.cent_country ?? 0),
                subscription_type: r.subscription_type || "",

                games: Number(r.games || 0),
                enrichment: Number(r.enrichment || 0),
                games_enrichment_db: Number(r.games_enrichment || 0), // active if = 1
                parent_app: Number(r.parent_app || 0),

                y1to13_students: Number(r.y1to13_students || 0),
                total_without_bundle: Number(r.total_without_bundle || 0),
            }));
            setServiceRowsRaw(mappedSvc);

            // licence rows
            const mappedLic = licData.map((r) => ({
                pk_cent_key: r.pk_cent_key || r.fk_cent_key,
                cent_name: r.cent_name,
                cent_payable_currency: r.cent_payable_currency || "",
                cent_fixed_fee_month: Number(r.cent_fixed_fee_month || 0),
                total_math_sessions: Number(r.total_math_sessions || 0),
                total_english_sessions: Number(r.total_english_sessions || 0),
                total_other_sessions: Number(r.total_other_sessions || 0),
                total_combined_sessions: Number(r.total_combined_sessions || 0),
                total_amount_payable: Number(r.total_amount_payable || 0),
                payment_status: Number(r.payment_status ?? 3),
                month_end_date: r.month_end_date || null,
                pk_cps_key: r.pk_cps_key || null,
                pk_cps_id: r.pk_cps_id || null,
                _raw: r,
            }));
            setLicenceRowsRaw(mappedLic);
        } catch (e) {
            toast.error("Failed to load report data");
            setServiceRowsRaw([]);
            setLicenceRowsRaw([]);
        } finally {
            setLoading(false);
        }
    }

    const mergedRows = useMemo(() => {
        const licByCent = new Map(licenceRowsRaw.map((x) => [x.pk_cent_key, x]));
        return serviceRowsRaw.map((svc) => {
            const lic = licByCent.get(svc.pk_cent_key);

            const subscribedToBundle = Number(svc.games_enrichment_db) > 0;
            const headcount = Number.isFinite(svc.y1to13_students) ? svc.y1to13_students : 0;
            const bundle = subscribedToBundle ? headcount * 4 : 0;

            const service_total =
                (Number.isFinite(svc.games) ? svc.games : 0) +
                (Number.isFinite(svc.enrichment) ? svc.enrichment : 0) +
                (Number.isFinite(svc.parent_app) ? svc.parent_app : 0) +
                bundle;

            const licence_total = lic ? Number(lic.total_amount_payable || 0) : 0;
            const combined_total = service_total + licence_total;

            return {
                id: svc.id,
                pk_cent_key: svc.pk_cent_key,
                cent_name: svc.cent_name,
                cent_country: svc.cent_country,
                subscription_type: svc.subscription_type,

                y1to13_students: svc.y1to13_students,

                // service columns
                games: svc.games,
                enrichment: svc.enrichment,
                games_enrichment: bundle,
                parent_app: svc.parent_app,
                service_total,

                // licensing
                cent_fixed_fee_month: lic?.cent_fixed_fee_month ?? 0,
                total_math_sessions: lic?.total_math_sessions ?? 0,
                total_english_sessions: lic?.total_english_sessions ?? 0,
                total_other_sessions: lic?.total_other_sessions ?? 0,
                total_combined_sessions: lic?.total_combined_sessions ?? 0,
                cent_payable_currency: lic?.cent_payable_currency ?? "",
                licence_total,
                payment_status: lic?.payment_status ?? 3,
                month_end_date: lic?.month_end_date ?? null,
                pk_cps_key: lic?.pk_cps_key ?? null,
                pk_cps_id: lic?.pk_cps_id ?? null,

                total: combined_total,
            };
        });
    }, [serviceRowsRaw, licenceRowsRaw]);


    const filteredRows = useMemo(() => {
        return mergedRows.filter((r) => {
            const t = (r.subscription_type || "").toLowerCase();
            if (tab === "none") return !t;
            return t === tab;
        });
    }, [mergedRows, tab]);

    const downloadInvoice = async (row) => {
        try {
            if (!row?.pk_cps_key || !row?.pk_cps_id || !row?.pk_cent_key) return;
            const response = await axios.post(
                BASE_URL + "API/api_index.php",
                {
                    security_key: SECURITY_KEY,
                    invoice_id1: row.pk_cps_id,
                    invoice_id: row.pk_cps_key,
                    pk_cent_key: row.pk_cent_key,
                    mode: "getDownloadcentreLicenseinvoiceCustom",
                },
                { responseType: "blob" }
            );
            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.style.display = "none";
            a.href = url;
            a.download = `IL_00${row.pk_cps_id}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error("Error downloading invoice:", error);
        }
    };

    const downloadReceipt = async (row) => {
        try {
            if (!row?.pk_cps_key || !row?.pk_cps_id || !row?.pk_cent_key) return;
            const response = await axios.post(
                BASE_URL + "API/api_index.php",
                {
                    security_key: SECURITY_KEY,
                    invoice_id1: row.pk_cps_id,
                    invoice_id: row.pk_cps_key,
                    pk_cent_key: row.pk_cent_key,
                    mode: "getDownloadcentreLicenseReceiptCustom",
                },
                { responseType: "blob" }
            );
            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.style.display = "none";
            a.href = url;
            a.download = `RL_00${row.pk_cps_id}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error("Error downloading receipt:", error);
        }
    };

    const submitPaid = async (e) => {
        e?.preventDefault?.();
        if (!paidData?.pk_cps_key || !paidData?.pk_cent_key) return;
        const form = new FormData(e.target);
        const lf_transacton_id = String(form.get("lf_transacton_id") || "").trim();
        if (!lf_transacton_id) return;
        setIsSubmittingPaid(true);
        try {
            const response = await axios.post(BASE_URL + "API/api_index.php", {
                security_key: SECURITY_KEY,
                mode: "addLiensceFeePaidCentre",
                lf_transacton_id,
                fk_cent_key: paidData.pk_cent_key,
                invoice_key: paidData.pk_cps_key,
                pk_super_admin_key: sessionStorage.getItem("key"),
            });

            const ok = response?.data?.success === true && response?.data?.data === 1;
            if (ok) {
                toast.success("Success! The amount has been paid successfully.", { autoClose: 700 });
                setPaidData(null);
                setShowModalPaid(false);
                await loadAll(refDate, billMonth);
            } else {
                toast.error("Failure! Unable to set as paid. Try again later", { autoClose: 700 });
            }
        } catch (error) {
            console.error("Error setting paid:", error);
            toast.error("Failure! Unable to set as paid. Try again later", { autoClose: 700 });
        } finally {
            setIsSubmittingPaid(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                field: "cent_name",
                headerName: "Centre",
                flex: 1.4,
                minWidth: 220,
                headerClassName: "super-app-theme--header",
            },
            {
                field: "subscription_type",
                headerName: "Subscription",
                flex: 1.0,
                minWidth: 150,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => formatSubscription(p.value),
            },

            /* =======================
             * HIDDEN FOR NOW (re-enable later if needed)
             * -----------------------
             * {
             *   field: "y1to13_students",
             *   headerName: "Y1–Y13 Students",
             *   type: "number",
             *   flex: 0.85,
             *   minWidth: 140,
             *   headerClassName: "super-app-theme--header",
             *   valueFormatter: (p) =>
             *     Number.isFinite(Number(p.value)) ? Number(p.value).toLocaleString() : "0",
             * },
             */

            /* Service pricing breakdown (hidden) */
            /*
            {
              field: "games",
              headerName: "Games",
              flex: 0.9,
              minWidth: 120,
              headerClassName: "super-app-theme--header",
              renderCell: (params) => formatMoney(params.row.games, params.row.cent_country),
            },
            {
              field: "enrichment",
              headerName: "Enrichment",
              flex: 0.95,
              minWidth: 130,
              headerClassName: "super-app-theme--header",
              renderCell: (params) => formatMoney(params.row.enrichment, params.row.cent_country),
            },
            */
            {
                field: "games_enrichment",
                headerName: "Games + Enrichment",
                flex: 1.1,
                minWidth: 180,
                headerClassName: "super-app-theme--header",
                renderCell: (params) =>
                    formatMoney(params.row.games_enrichment, params.row.cent_country),
            },
            {
                field: "parent_app",
                headerName: "Parent App",
                flex: 0.95,
                minWidth: 130,
                headerClassName: "super-app-theme--header",
                renderCell: (params) => formatMoney(params.row.parent_app, params.row.cent_country),
            },

            // Licensing breakdown (details)
            {
                field: "cent_fixed_fee_month",
                headerName: "Fixed Fees",
                flex: 0.9,
                minWidth: 130,
                headerClassName: "super-app-theme--header",
                renderCell: (p) => formatMoney(p.row.cent_fixed_fee_month, p.row.cent_country),
            },
            {
                field: "total_math_sessions",
                headerName: "No. of Maths Scheduled sessions",
                flex: 1.2,
                minWidth: 210,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => Number(p.value || 0).toString(),
            },
            {
                field: "total_english_sessions",
                headerName: "No. of English Scheduled Sessions",
                flex: 1.1,
                minWidth: 200,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => Number(p.value || 0).toString(),
            },
            {
                field: "total_other_sessions",
                headerName: "No. of Other Subject Scheduled sessions",
                flex: 1.2,
                minWidth: 210,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => Number(p.value || 0).toString(),
            },
            {
                field: "total_combined_sessions",
                headerName: "No. of Combined Subject Scheduled sessions",
                flex: 1.2,
                minWidth: 210,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => Number(p.value || 0).toString(),
            },
            {
                field: "cent_payable_currency",
                headerName: "Payment Currency",
                flex: 0.9,
                minWidth: 130,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => (p.value ? String(p.value) : "—"),
            },
            {
                field: "payment_status",
                headerName: "Payment Status",
                flex: 0.9,
                minWidth: 140,
                headerClassName: "super-app-theme--header",
                renderCell: (params) => {
                    const row = params.row;
                    if (parseInt(row.payment_status) === 3) {
                        return (
                            <div className="d-flex pad-12">
                                <span className="invoice-available-msg">-</span>
                            </div>
                        );
                    } else {
                        return (
                            <div className="d-flex pad-12">
                                {parseInt(row.payment_status) === 2 ? (
                                    <span className="active-btn my-auto cursor-pointer">
                    <DoneIcon className="icon-blue btn-icon me-1" />
                    Paid
                  </span>
                                ) : (
                                    <span className="deactive-btn my-auto cursor-pointer">
                    <CloseIcon className="btn-icon me-1" />
                    Unpaid
                  </span>
                                )}
                            </div>
                        );
                    }
                },
            },
            {
                field: "action",
                headerName: "Action",
                flex: 1.1,
                minWidth: 250,
                headerClassName: "super-app-theme--header",
                renderCell: (params) => {
                    const row = params.row;
                    if (parseInt(row.payment_status) === 3) {
                        return (
                            <div className="d-flex pad-12">
                <span className="invoice-available-msg">
                  Invoice Available for Past Months
                </span>
                            </div>
                        );
                    } else {
                        return (
                            <div className="d-flex pad-12">
                                <Buttonmui
                                    variant="outlined-red"
                                    onClick={() => downloadInvoice(row)}
                                    className="primary-blue btnmr-2 my-2"
                                >
                                    Invoice
                                </Buttonmui>
                                {parseInt(row.payment_status) === 2 ? (
                                    <Buttonmui
                                        variant="outlined-red"
                                        onClick={() => downloadReceipt(row)}
                                        className="primary-blue btnmr-2 my-2"
                                    >
                                        Receipt
                                    </Buttonmui>
                                ) : (
                                    <Buttonmui
                                        variant="outlined-red"
                                        onClick={() => {
                                            setPaidData(row);
                                            setShowModalPaid(true);
                                        }}
                                        className="primary-blue my-2"
                                    >
                                        Set as Paid
                                    </Buttonmui>
                                )}
                            </div>
                        );
                    }
                },
            },
            {
                field: "service_total",
                headerName: "Service Total",
                flex: 1.0,
                minWidth: 150,
                headerClassName: "super-app-theme--header",
                renderCell: (params) =>
                    formatMoney(params.row.service_total, params.row.cent_country),
            },
            {
                field: "licence_total",
                headerName: "Licencing Total",
                flex: 1.0,
                minWidth: 150,
                headerClassName: "super-app-theme--header",
                renderCell: (p) => formatMoney(p.row.licence_total, p.row.cent_country),
            },
            {
                field: "total",
                headerName: "Combined Total",
                flex: 1.05,
                minWidth: 160,
                headerClassName: "super-app-theme--header",
                renderCell: (params) => formatMoney(params.row.total, params.row.cent_country),
            },
        ],
        []
    );

    // currency totals
    const grandTotals = useMemo(() => {
        let uk = 0;
        let nonUk = 0;
        for (const r of filteredRows) {
            if (COUNTRY_CURRENCY_POUNDS.includes(Number(r.cent_country))) uk += Number(r.total) || 0;
            else nonUk += Number(r.total) || 0;
        }
        return { uk, nonUk };
    }, [filteredRows]);

    const csvBase = `Centre_Service_Prices_and_Licensing_${refDate}_${billMonth}_${tab}`;

    return (
        <div className="container">
            {/* Payment modal */}
            <Modal show={showModalPaid} onHide={toggleModalPaid} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <h5 className="page-title">Payment</h5>
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={submitPaid}>
                    <Modal.Body>
                        <div className="row">
                            <div className="col-lg-6 col-md-6">
                                <Card className="rounded-1 mt-2 card-bdr h-100">
                                    <Card.Body>
                                        <div className="cus-pyt-dtls">
                                            <h6 className="mb-0">Centre Name:</h6>
                                            <p className="mb-0">{paidData?.cent_name || ""}</p>
                                        </div>
                                        <div className="cus-pyt-dtls">
                                            <h6 className="mb-0">Billing Month:</h6>
                                            <p className="mb-0">
                                                {paidData?.month_end_date
                                                    ? new Date(paidData.month_end_date).toLocaleString("default", {
                                                        month: "long",
                                                        year: "numeric",
                                                    })
                                                    : ""}
                                            </p>
                                        </div>
                                        <div className="cus-pyt-dtls">
                                            <h6>Total Amount:</h6>
                                            <p className="mb-0">
                                                {paidData
                                                    ? formatMoney(paidData.licence_total, paidData.cent_country)
                                                    : ""}
                                            </p>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                            <div className="col-lg-6 col-md-6">
                                <Card className="rounded-1 mt-2 card-bdr h-100">
                                    <Card.Body>
                                        <div className="cus-pyt-dtls mb-2">
                                            <h6 className="mb-0">Payment Type:</h6>
                                            <p className="mb-0">Offline</p>
                                        </div>
                                        <div className="mt-3">
                                            <h6 className="form-lable required-field">Transaction ID</h6>
                                            <Form.Control
                                                type="text"
                                                id="lf_transacton_id"
                                                name="lf_transacton_id"
                                                required
                                            />
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="pt-0">
                        <div className="mx-auto d-flex gap-2">
                            <Button variant="contained" className="primary-gray" onClick={toggleModalPaid}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                type="submit"
                                className="primary-filled"
                                disabled={isSubmittingPaid}
                            >
                                Submit
                            </Button>
                        </div>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Header */}
            <div className="mb-3 d-flex justify-content-between align-items-center">
                <div>
                    <h5 className="page-title">Centre Service Prices & Licensing</h5>
                    <Breadcrumbs aria-label="breadcrumb" className="my-breadcrumbs">
                        <NavLink to="/calendar">Home</NavLink>
                        <NavLink to="/admin/AdminReport">Reports</NavLink>
                        <Typography color="text.primary">Service + Licensing</Typography>
                    </Breadcrumbs>
                </div>

                {/* Filters (locked together) */}
                <div className="d-flex gap-2 align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <label className="mb-0">As of (pricing):</label>
                        <input
                            type="date"
                            className="form-control"
                            style={{ width: 160 }}
                            value={refDate}
                            onChange={(e) => setRefDate(e.target.value)}
                            title="As of date"
                        />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <label className="mb-0">Billing Month:</label>
                        <input
                            type="month"
                            className="form-control"
                            style={{ width: 140 }}
                            value={billMonth}
                            onChange={(e) => setBillMonth(e.target.value)}
                            title="Billing Month"
                        />
                    </div>
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() => loadAll(refDate, billMonth)}
                        disabled={loading}
                    >
                        {loading ? "Refreshing…" : "Apply"}
                    </button>
                </div>
            </div>

            {/* Tabs / filters */}
            <div className="mb-3">
                <div className="btn-group" role="group" aria-label="Subscription tabs">
                    <button
                        type="button"
                        className={`btn ${tab === "trial" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setTab("trial")}
                    >
                        Trial
                    </button>
                    <button
                        type="button"
                        className={`btn ${tab === "annual" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setTab("annual")}
                    >
                        Annual
                    </button>
                    <button
                        type="button"
                        className={`btn ${tab === "monthly" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setTab("monthly")}
                    >
                        Monthly
                    </button>
                    <button
                        type="button"
                        className={`btn ${tab === "none" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setTab("none")}
                    >
                        No Add-on Subscription
                    </button>
                </div>
            </div>

            <Card className="card my-card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <strong>
                        As of {dayjs(refDate).format("YYYY-MM-DD")} —{" "}
                        {tab === "none" ? "Add-on Subscription" : formatSubscription(tab)} — Billing {billMonth}
                    </strong>
                    <span className="text-muted">
            {grandTotals.uk > 0 && grandTotals.nonUk > 0 ? (
                <>
                    Grand Totals:&nbsp;
                    <b>£{grandTotals.uk.toLocaleString()}</b> (UK)&nbsp;•&nbsp;
                    <b>${grandTotals.nonUk.toLocaleString()}</b> (Non-UK)
                </>
            ) : Number(grandTotals.uk) > 0 ? (
                <>Grand Total: <b>£{grandTotals.uk.toLocaleString()}</b></>
            ) : (
                <>Grand Total: <b>${grandTotals.nonUk.toLocaleString()}</b></>
            )}
          </span>
                </div>
                <Card.Body>
                    <Box
                        sx={{
                            width: "100%",
                            overflowX: "auto",
                            "& .super-app-theme--header": {
                                backgroundColor: "#f4f4f4",
                                whiteSpace: "normal",
                            },
                            "& .MuiDataGrid-cell": {
                                whiteSpace: "normal",
                                wordWrap: "break-word",
                            },
                            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
                                outline: "none",
                            },
                        }}
                        style={{ height: GRID_HEIGHT }}
                    >
                        <DataGrid
                            autoHeight
                            loading={loading}
                            rows={filteredRows}
                            columns={columns}
                            pageSizeOptions={[10, 25, 50, 100]}
                            rowHeight={56}
                            slots={{ loadingOverlay: LinearProgress, toolbar: GridToolbar }}
                            slotProps={{
                                toolbar: {
                                    csvOptions: { fileName: csvBase },
                                    printOptions: {
                                        fileName: csvBase,
                                        hideFooter: true,
                                        hideToolbar: true,
                                    },
                                    showQuickFilter: true,
                                },
                            }}
                            disableRowSelectionOnClick
                            showColumnVerticalBorder
                            showCellVerticalBorder
                        />
                    </Box>
                </Card.Body>
            </Card>
        </div>
    );
}
